import React, { useState } from 'react'
import { ServiceItem, ServiceAction } from '../../interfaces/services'
import ArtisanService from '../../api/artisan-services'
import ServiceRequest from '../../api/service-request-service'

export const getStatusClass = (status: ServiceItem['status']) => {
  switch (status) {
    case 'pending':
      return 'text-red-500'
    case 'assigned':
      return 'text-orange-500'
    case 'in_progress':
      return 'text-blue-500'
    case 'completed':
      return 'text-green-500'
    case 'cancelled':
      return 'text-gray-500'
    default:
      return ''
  }
}

export const getStatusLabel = (status: ServiceItem['status']) => {
  switch (status) {
    case 'pending':
      return 'Pending'
    case 'assigned':
      return 'Assigned'
    case 'in_progress':
      return 'In Progress'
    case 'completed':
      return 'Completed'
    case 'cancelled':
      return 'Cancelled'
    default:
      return status
  }
}

interface Props {
  selectedService: ServiceItem | null
  selectedAction: ServiceAction
  selectedArtisan: string
  setSelectedArtisan: (artisanId: string) => void
  sendViaEmail: boolean
  setSendViaEmail: (value: boolean) => void
  sendViaText: boolean
  setSendViaText: (value: boolean) => void
  onStatusUpdated?: () => void
}

const RenderModalContent: React.FC<Props> = ({
  selectedService: initialService,
  selectedAction,
  selectedArtisan,
  setSelectedArtisan,
  sendViaEmail,
  setSendViaEmail,
  sendViaText,
  setSendViaText,
  onStatusUpdated,
}) => {
  const [selectedService, setSelectedService] = useState<ServiceItem | null>(
    initialService
  )

  // ---- ARTISANS FETCHING (for selected service) ----
  const { data: artisansResponse, isValidating: artisansLoading } =
    ArtisanService.useGetArtisans({
      page: 1,
      size: 100,
      serviceId: selectedService?.serviceId,
      search: undefined,
      estateId: undefined,
    })

  const artisans = artisansResponse?.results ?? []

  if (!selectedService) return null

  const InfoRow = ({ label, value }: { label: string; value: string }) => (
    <div className="mb-3">
      <span className="text-gray-600 text-sm">{label}: </span>
      <span className="text-gray-900 font-medium">{value}</span>
    </div>
  )

  const handleUpdateToProgress = async () => {
    try {
      await ServiceRequest.updateRequestStatus(selectedService.id, 'in_progress')
      // Update local state immediately so UI reflects change instantly
      setSelectedService((prev) => (prev ? { ...prev, status: 'in_progress' } : prev))
      // Tell parent to refetch the list in the background
      onStatusUpdated?.()
    } catch (error) {
      console.error('Failed to update status:', error)
    }
  }

  const commonInfo = (
    <>
      <InfoRow label="Name of initiator" value={selectedService.initiatorName} />
      <InfoRow label="Phone number" value={selectedService.phoneNumber || 'N/A'} />
      <InfoRow label="House Address" value={selectedService.houseAddress || 'N/A'} />
      <InfoRow label="Request code" value={selectedService.requestCode || 'N/A'} />
      {(selectedAction === 'viewDetails' ||
        selectedAction === 'reassign' ||
        selectedAction === 'cancelRequest') && (
        <div className="mb-3 flex items-center justify-between">
          <div>
            <span className="text-gray-600 text-sm">Request Status: </span>
            <span className={`font-medium ${getStatusClass(selectedService.status)}`}>
              {getStatusLabel(selectedService.status)}
            </span>
          </div>
          {selectedAction === 'viewDetails' && (
            <button
              onClick={handleUpdateToProgress}
              disabled={selectedService.status !== 'assigned'}
              className={`text-sm px-4 py-2 rounded-lg font-normal transition-colors duration-200 
                ${
                  selectedService.status === 'assigned'
                    ? 'bg-black text-white hover:bg-gray-800 cursor-pointer rounded-lg'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
            >
              Update to Progress
            </button>
          )}
        </div>
      )}
    </>
  )

  switch (selectedAction) {
    case 'viewDetails':
      return (
        <div className="space-y-4">
          {commonInfo}
          <div className="pt-4 border-t">
            <label className="block text-sm text-gray-600 mb-2">Service type</label>
            <input
              type="text"
              value={selectedService.serviceType}
              disabled
              className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-2">Assigned Artisan</label>
            <input
              type="text"
              value={selectedService.artisanId?.id || selectedService.artisanId?.fullName || 'Not assigned'}
              disabled
              className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
            />
          </div>
          <div className="flex items-center justify-between py-2">
            <span className="text-sm text-gray-700">Send via email</span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={sendViaEmail}
                onChange={(e) => setSendViaEmail(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-black"></div>
            </label>
          </div>
          <div className="flex items-center justify-between py-2">
            <span className="text-sm text-gray-700">Send via text message</span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={sendViaText}
                onChange={(e) => setSendViaText(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-black"></div>
            </label>
          </div>
        </div>
      )

    case 'assignArtisan':
    case 'reassign':
      return (
        <div className="space-y-4">
          {commonInfo}
          <div className="pt-4 border-t">
            <label className="block text-sm text-gray-600 mb-2">Service type</label>
            <input
              type="text"
              value={selectedService.serviceType}
              disabled
              className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-600 mb-2">
              Select {selectedService.serviceType} Artisan
            </label>

            {artisansLoading ? (
              <div className="text-sm text-gray-600 py-2">Loading artisans...</div>
            ) : artisans.length === 0 ? (
              <div className="text-sm text-gray-600 py-2">
                No artisans available for this service
              </div>
            ) : (
              <select
                value={selectedArtisan}
                onChange={(e) => setSelectedArtisan(e.target.value)}
                className="w-full px-6 py-2 border border-gray-300 rounded-lg focus:outline-none"
              >
                <option value="">Select</option>
                {artisans.map((a: any) => {
                  const id = a.id ?? a._id ?? String(Date.now())
                  const name = a.fullName ?? a.name ?? 'Unnamed artisan'
                  return (
                    <option key={id} value={id}>
                      {name}
                    </option>
                  )
                })}
              </select>
            )}
          </div>

          <div className="flex items-center justify-between py-2 px-2">
            <span className="text-sm text-gray-700">Send via email</span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={sendViaEmail}
                onChange={(e) => setSendViaEmail(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-black"></div>
            </label>
          </div>

          <div className="flex items-center justify-between py-2 px-2">
            <span className="text-sm text-gray-700">Send via text message</span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={sendViaText}
                onChange={(e) => setSendViaText(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-black"></div>
            </label>
          </div>
        </div>
      )

    case 'cancelRequest':
      return (
        <div className="space-y-4">
          {commonInfo}
          <div className="pt-4 border-t">
            <label className="block text-sm text-gray-600 mb-2">Service type</label>
            <input
              type="text"
              value={selectedService.serviceType}
              disabled
              className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-2">Assigned Artisan</label>
            <input
              type="text"
              value={selectedService.artisanId?.id || selectedService.artisanId?.fullName || 'Not assigned'}
              disabled
              className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
            />
          </div>
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800">
              Are you sure you want to cancel this service request? This action cannot be
              undone.
            </p>
          </div>
        </div>
      )

    default:
      return null
  }
}

export default RenderModalContent
